import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import { config } from '../configuration/config';
import { extractLanguage } from '../store/lang/lang';
import { selectLocale } from '../store/lang/langSelectors';
import { configureFetchDisplayMessages } from '../store/lang/langServices';
import {
    type SessionRenewedResult,
    configureMockUserManager,
    configureUserManager,
    createUserManager,
} from '../store/login/loginServices';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from '../store/login/loginSlice';
import { accessToken } from '../store/tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from '../store/tokenHandling/tokenHandlingSlice';
import { initIntl } from './setup/intl';
import { attemptInitialSignIn } from './setup/oauth';
import { store } from './setup/store';
import { trace } from './setup/trace';

export interface OAuthConfig {
    onSessionExpired: () => void;
    onSessionRenewed: (result: SessionRenewedResult) => void;
}

export const main = async (renderApp: () => void) => {
    const fetchDisplayMessages = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    const unsubscribeHandler = store.subscribe(() => {
        const lang = extractLanguage(selectLocale(store.getState()));
        const html = document.querySelector('html');

        if (html && lang && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: (result: SessionRenewedResult) => {
            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));
            store.dispatch(userSessionRenewed());

            fetchDisplayMessages(result.locale);
        },
    } as OAuthConfig;

    // enables mocking of authentication
    const userManager = config.login.mockAuthorization
        ? configureMockUserManager(oauthConfig)
        : configureUserManager(oauthConfig, createUserManager());

    const signinSilent = userManager.signinSilent.bind(userManager);
    document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, () => signinSilent());
    document.addEventListener(EVENT_USER_PROFILE_CHANGED, () => signinSilent());

    try {
        await userManager.clearStaleState();
        await attemptInitialSignIn(userManager);
        initIntl(store);
        renderApp();
    } catch (error) {
        trace.error('could not start application', error);
        unsubscribeHandler();
    }
};
